/**
 * Form definitions for each notification provider.
 *
 * The server validates config against its own zod schema per provider
 * (server/src/notifications/providers/*.ts); this is the UI's view of the
 * same shape — labels, hints, which fields are credentials. `GET
 * /notifications/types` remains the list of what the server actually offers,
 * so a type listed there but missing here still gets a raw JSON editor.
 */

export type FieldKind = 'text' | 'url' | 'password' | 'number' | 'textarea' | 'checkbox' | 'select' | 'headers' | 'numberlist';

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  /** Applied when the form opens for a new channel. */
  defaultValue?: string | number | boolean;
  options?: { value: string; label: string }[];
  /**
   * Credential. Comes back masked from the API; the form shows it blank with a
   * "stored" hint, and leaving it blank keeps the saved value.
   */
  secret?: boolean;
  mono?: boolean;
}

export interface ProviderSpec {
  type: string;
  label: string;
  description: string;
  /** Setup pointer shown above the fields. */
  help?: string;
  fields: FieldSpec[];
}

const webhookUrl = (label: string, placeholder: string, hint?: string): FieldSpec => ({
  key: 'webhook_url', label, kind: 'url', required: true, secret: true, placeholder, hint, mono: true,
});

export const PROVIDERS: ProviderSpec[] = [
  {
    type: 'slack',
    label: 'Slack',
    description: 'Incoming webhook — a coloured attachment per event.',
    help: 'Slack → Apps → Incoming Webhooks → Add to channel, then paste the URL.',
    fields: [webhookUrl('Webhook URL', 'https://hooks.slack.com/services/T…/B…/…')],
  },
  {
    type: 'discord',
    label: 'Discord',
    description: 'Channel webhook — one embed per event.',
    help: 'Channel settings → Integrations → Webhooks → New Webhook → Copy URL.',
    fields: [webhookUrl('Webhook URL', 'https://discord.com/api/webhooks/…')],
  },
  {
    type: 'teams',
    label: 'Microsoft Teams',
    description: 'Adaptive Card via a Workflows (Power Automate) or legacy connector webhook.',
    help: 'In the channel: Workflows → "Post to a channel when a webhook request is received", then copy the URL.',
    fields: [webhookUrl('Webhook URL', 'https://prod-….logic.azure.com/…')],
  },
  {
    type: 'telegram',
    label: 'Telegram',
    description: 'Bot message to a chat, group or channel.',
    help: 'Create a bot with @BotFather for the token; the chat id comes from @userinfobot or the getUpdates API.',
    fields: [
      { key: 'bot_token', label: 'Bot token', kind: 'password', required: true, secret: true, placeholder: '123456789:AA…', mono: true },
      { key: 'chat_id', label: 'Chat ID', kind: 'text', required: true, placeholder: '-1001234567890', mono: true },
      { key: 'api_base', label: 'API base (optional)', kind: 'url', placeholder: 'https://api.telegram.org', hint: 'Only for a self-hosted Bot API server.' },
    ],
  },
  {
    type: 'ntfy',
    label: 'ntfy',
    description: 'Push to an ntfy topic, with priority from the event severity.',
    fields: [
      { key: 'server_url', label: 'Server URL', kind: 'url', required: true, defaultValue: 'https://ntfy.sh', placeholder: 'https://ntfy.sh' },
      { key: 'topic', label: 'Topic', kind: 'text', required: true, placeholder: 'pulsecheck-alerts', mono: true },
      { key: 'token', label: 'Access token (optional)', kind: 'password', secret: true, hint: 'For protected topics.', mono: true },
    ],
  },
  {
    type: 'gotify',
    label: 'Gotify',
    description: 'Message to a Gotify server, rendered as markdown.',
    help: 'Gotify → Apps → Create application, then copy the token.',
    fields: [
      { key: 'server_url', label: 'Server URL', kind: 'url', required: true, placeholder: 'https://gotify.example.com' },
      { key: 'app_token', label: 'Application token', kind: 'password', required: true, secret: true, mono: true },
    ],
  },
  {
    type: 'pushover',
    label: 'Pushover',
    description: 'Push to your devices; outages send at high priority.',
    help: 'The user key is on your Pushover dashboard; create an application for the API token.',
    fields: [
      { key: 'user_key', label: 'User key', kind: 'password', required: true, secret: true, mono: true },
      { key: 'app_token', label: 'API token', kind: 'password', required: true, secret: true, mono: true },
      { key: 'device', label: 'Device (optional)', kind: 'text', placeholder: 'all devices' },
      { key: 'sound', label: 'Sound (optional)', kind: 'text', placeholder: 'pushover' },
    ],
  },
  {
    type: 'email',
    label: 'Email (SMTP)',
    description: 'Plain-text and HTML email through your own SMTP server.',
    fields: [
      { key: 'host', label: 'SMTP host', kind: 'text', required: true, placeholder: 'smtp.example.com' },
      { key: 'port', label: 'Port', kind: 'number', defaultValue: 587, placeholder: '587' },
      { key: 'secure', label: 'TLS from the first byte (port 465)', kind: 'checkbox', defaultValue: false, hint: 'Off uses STARTTLS when the server offers it — the usual choice for 587.' },
      { key: 'username', label: 'Username (optional)', kind: 'text' },
      { key: 'password', label: 'Password (optional)', kind: 'password', secret: true },
      { key: 'from', label: 'From', kind: 'text', required: true, placeholder: 'PulseCheck <alerts@example.com>' },
      { key: 'to', label: 'To', kind: 'text', required: true, placeholder: 'ops@example.com, oncall@example.com', hint: 'Comma-separated.' },
      { key: 'subject_prefix', label: 'Subject prefix (optional)', kind: 'text', placeholder: '[PulseCheck]' },
    ],
  },
  {
    type: 'webhook',
    label: 'Webhook',
    description: 'Generic HTTP request. JSON by default, or your own body template; deliveries can be HMAC-signed.',
    fields: [
      { key: 'url', label: 'URL', kind: 'url', required: true, secret: true, placeholder: 'https://example.com/hooks/pulsecheck', hint: 'Treated as a credential: encrypted at rest and masked after saving.', mono: true },
      { key: 'method', label: 'Method', kind: 'select', defaultValue: 'POST', options: ['POST', 'PUT', 'PATCH', 'GET', 'DELETE'].map(m => ({ value: m, label: m })) },
      { key: 'headers', label: 'Headers (JSON)', kind: 'headers', secret: true, hint: 'Credential headers show as •••••••• and stay unchanged unless you replace them.' },
      {
        key: 'body_template', label: 'Body template (optional)', kind: 'textarea', mono: true,
        placeholder: '{"text": "{{service_name}} is {{status}}"}',
        hint: 'Leave blank for the standard JSON payload. Variables: {{service_name}}, {{service_url}}, {{service_id}}, {{status}}, {{previous_status}}, {{alert_type}}, {{message}}, {{timestamp}}, {{error_message}}, {{status_code}}, {{response_time}}, {{incident_id}}, {{duration}}, {{duration_human}}, {{tags}}, {{group}}, {{dashboard_url}}. Values are JSON-escaped when the template is JSON; add |raw or |json to override.',
      },
      { key: 'secret', label: 'Signing secret (optional)', kind: 'password', secret: true, hint: 'When set, deliveries carry X-PulseCheck-Signature: t=<unix>,v1=<HMAC-SHA256 of "t.body"> so the receiver can verify them.', placeholder: 'e.g. a 32+ character random string' },
    ],
  },
  {
    type: 'apprise',
    label: 'Apprise',
    description: 'Bridge to an Apprise API server — one channel, ninety-odd downstream services.',
    help: 'Give Apprise URLs directly, or the key of a config stored on the Apprise server.',
    fields: [
      { key: 'server_url', label: 'Apprise API URL', kind: 'url', required: true, placeholder: 'http://apprise:8000' },
      { key: 'urls', label: 'Apprise URLs', kind: 'textarea', secret: true, mono: true, placeholder: 'mailto://user:pass@example.com\ntgram://bottoken/chatid', hint: 'One per line or comma-separated. Or leave blank and use a config key.' },
      { key: 'config_key', label: 'Config key', kind: 'text', placeholder: 'ops', mono: true },
      { key: 'tag', label: 'Tag (optional)', kind: 'text', hint: 'Only notify the URLs in the stored config carrying this tag.' },
    ],
  },
  {
    type: 'browser',
    label: 'Browser push',
    description: 'Native notifications on devices that opted in from their Profile page — even with the tab closed.',
    help: 'Each person enables it on each device under Profile → Browser notifications. This channel decides which events reach them.',
    fields: [
      { key: 'user_ids', label: 'Only these users (optional)', kind: 'numberlist', placeholder: '1, 4', hint: 'User ids, comma-separated (Users page). Blank = every subscribed device.', mono: true },
    ],
  },
];

export const providerByType = new Map(PROVIDERS.map(p => [p.type, p]));

/** Field defaults for a new channel of this type. */
export function defaultConfig(type: string): Record<string, unknown> {
  const spec = providerByType.get(type);
  const out: Record<string, unknown> = {};
  for (const f of spec?.fields ?? []) {
    if (f.defaultValue !== undefined) out[f.key] = f.defaultValue;
  }
  return out;
}
